import { BLEND_MODES, Container, Sprite, Texture } from "pixi.js"

// Firelight for the screens a player meets before the game: the login screen and character creation.
// Both are about the same thing -- a fire in the dark, and what it is holding back -- so they share
// their light.
//
// Everything here is SPRITES over a handful of small gradient textures baked once on a canvas, never
// a filter: a filter is an offscreen render pass per object, which is the first thing to cost frames
// on the budget machines this game targets. A soft glow is one tinted sprite; forty embers are forty
// sprites over one texture, which pixi batches into a single draw.
//
// Anything that EMITS light is additive. A normal-blend orange wash over the forest reads as dust;
// added, it brightens toward yellow and white where it piles up, which is what firelight does.

// Light that adds onto grass must be starved of green or it lands on lime. This is the additive-only
// ember colour; the hotter ones are for embers small enough not to tint anything.
export const BLAZE = 0xff3d08
export const EMBER = 0xff8a24
export const GOLD = 0xffd166
export const CREAM = 0xffedc7

// A spark cools as it rises: near-white-gold when it leaves the fire, deep orange by the time it goes.
const SPARK_HOT = 0xffe7a3
const SPARK_WARM = 0xffb347
const SPARK_COOL = 0xff6a24

type Paint = (g: CanvasRenderingContext2D, w: number, h: number) => void

const bake = (w: number, h: number, paint: Paint): Texture => {
    try {
        const canvas = document.createElement("canvas")
        canvas.width = w
        canvas.height = h
        const g = canvas.getContext("2d")
        if (!g || typeof g.createRadialGradient !== "function") {
            return Texture.WHITE
        }
        paint(g, w, h)
        return Texture.from(canvas)
    } catch (e) {
        // No canvas (a test environment, or a browser that refuses one): a flat texture costs the
        // softness, not the screen.
        return Texture.WHITE
    }
}

const radial = (stops: [number, number][]): Paint => (g, w, h) => {
    const gradient = g.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2)
    stops.forEach(([offset, alpha]) => gradient.addColorStop(offset, `rgba(255,255,255,${alpha})`))
    g.fillStyle = gradient
    g.fillRect(0, 0, w, h)
}

const cache: Record<string, Texture> = {}
const once = (key: string, make: () => Texture): Texture => (cache[key] = cache[key] || make())

/** A white disc that falls off to nothing: tint it and stretch it into any pool of light. */
export const softGlowTexture = (): Texture =>
    once("glow", () => bake(128, 128, radial([[0, 1], [0.3, 0.5], [0.65, 0.14], [1, 0]])))

/** A small hot core with a short halo -- an ember, rather than a light. */
export const sparkTexture = (): Texture =>
    once("spark", () => bake(32, 32, radial([[0, 1], [0.18, 1], [0.3, 0.55], [0.55, 0.12], [1, 0]])))

/** Clear in the middle, dark at the rim. Stretched over a screen it pushes the edges back. */
export const vignetteTexture = (): Texture =>
    once("vignette", () =>
        bake(256, 256, (g, w, h) => {
            const gradient = g.createRadialGradient(w / 2, h * 0.55, w * 0.22, w / 2, h * 0.55, w * 0.75)
            gradient.addColorStop(0, "rgba(0,0,0,0)")
            gradient.addColorStop(1, "rgba(3,2,1,0.9)")
            g.fillStyle = gradient
            g.fillRect(0, 0, w, h)
        }),
    )

/**
 * Night coming down over a scene: dark at the top, a lighter band through the middle where the action
 * is, dark again at the floor. Stretched to the screen; 4px wide is plenty for a vertical ramp.
 */
export const nightfallTexture = (): Texture =>
    once("nightfall", () =>
        bake(4, 256, (g, w, h) => {
            const gradient = g.createLinearGradient(0, 0, 0, h)
            gradient.addColorStop(0, "rgba(9,6,5,0.86)")
            gradient.addColorStop(0.42, "rgba(14,9,6,0.42)")
            gradient.addColorStop(0.72, "rgba(14,9,6,0.38)")
            gradient.addColorStop(1, "rgba(6,4,3,0.7)")
            g.fillStyle = gradient
            g.fillRect(0, 0, w, h)
        }),
    )

/** A pool of light: a stretched glow, centred on (x, y), additive unless told otherwise. */
export const glowSprite = (
    color: number,
    width: number,
    height: number,
    alpha: number,
    { additive = true }: { additive?: boolean } = {},
): Sprite => {
    const sprite = new Sprite(softGlowTexture())
    sprite.anchor.set(0.5)
    sprite.tint = color
    sprite.width = width
    sprite.height = height
    sprite.alpha = alpha
    if (additive) {
        sprite.blendMode = BLEND_MODES.ADD
    }
    return sprite
}

// ---- pure motion ------------------------------------------------------------------------------

/**
 * Firelight flicker, roughly -1..1. Three incommensurate sines, so it never visibly repeats, and
 * slow enough to read as a fire breathing rather than a bulb failing. `seed` decorrelates two lights.
 */
export const flicker = (ms: number, seed: number = 0): number =>
    0.55 * Math.sin(ms / 170 + seed) * Math.sin(ms / 410 + seed * 1.7) +
    0.3 * Math.sin(ms / 93 + seed * 2.3) +
    0.15 * Math.sin(ms / 1300 + seed)

/**
 * An ember's opacity over its life (age 0..1): it catches quickly, holds, and fades out over the last
 * stretch of its climb rather than winking out.
 */
export const emberEnvelope = (age: number): number => {
    if (age <= 0 || age >= 1) {
        return 0
    }
    const catchUp = Math.min(1, age / 0.12)
    const fade = age < 0.6 ? 1 : 1 - (age - 0.6) / 0.4
    return catchUp * fade
}

const lerpChannel = (from: number, to: number, shift: number, t: number) =>
    Math.round(((from >> shift) & 0xff) + (((to >> shift) & 0xff) - ((from >> shift) & 0xff)) * t)

const lerpColor = (from: number, to: number, t: number): number =>
    (lerpChannel(from, to, 16, t) << 16) | (lerpChannel(from, to, 8, t) << 8) | lerpChannel(from, to, 0, t)

/** Hot to warm over the first half of a spark's life, warm to cooling over the second. */
export const sparkColor = (age: number): number =>
    age < 0.5 ? lerpColor(SPARK_HOT, SPARK_WARM, age * 2) : lerpColor(SPARK_WARM, SPARK_COOL, (age - 0.5) * 2)

// ---- rising embers ----------------------------------------------------------------------------

interface Ember {
    sprite: Sprite
    x: number
    y: number
    rise: number
    swayAmp: number
    swayHz: number
    phase: number
    life: number
    maxLife: number
    size: number
}

export interface EmberDriftOptions {
    width: number
    height: number
    count: number
    /** How much of the height, measured up from the floor, embers are born in. */
    spawnBand?: number
    minSize?: number
    maxSize?: number
    /** Climb speed, px/s. */
    minRise?: number
    maxRise?: number
    /** Peak side-to-side drift, px. */
    sway?: number
    /** Seconds an ember lives. */
    minLife?: number
    maxLife?: number
    alpha?: number
    random?: () => number
}

/**
 * Embers lifting off a fire somewhere below the frame. A fixed pool: when one goes out it is reborn
 * at the floor, so the field never thins and never allocates after construction.
 *
 * Calm by default -- a few dozen slow sparks is atmosphere, a few hundred fast ones is an alarm. A
 * `surge` is the one loud moment it has: a gust that lifts the whole field for a second, for the
 * press that sends the player on.
 */
export class EmberDrift extends Container {
    private embers: Ember[] = []
    private opts: Required<EmberDriftOptions>
    private surgeLeft = 0
    private surgeMs = 1

    constructor(options: EmberDriftOptions) {
        super()
        this.opts = {
            spawnBand: 0.35,
            minSize: 3,
            maxSize: 8,
            minRise: 14,
            maxRise: 34,
            sway: 14,
            minLife: 3,
            maxLife: 7,
            alpha: 1,
            random: Math.random,
            ...options,
        }
        for (let i = 0; i < this.opts.count; i++) {
            const sprite = new Sprite(sparkTexture())
            sprite.anchor.set(0.5)
            sprite.blendMode = BLEND_MODES.ADD
            this.addChild(sprite)
            const ember = { sprite } as Ember
            this.spawn(ember)
            // Start the field already alight: every ember part way through a life, so the first frame
            // is not a line of sparks all leaving the floor at once.
            ember.life = this.opts.random() * ember.maxLife
            ember.y -= ember.rise * ember.life
            this.embers.push(ember)
        }
        this.paint()
    }

    get count(): number {
        return this.embers.length
    }

    resize = (width: number, height: number) => {
        this.opts.width = width
        this.opts.height = height
    }

    /** A gust: for `ms`, the field climbs faster and burns brighter, easing back to calm. */
    surge = (ms: number = 1200) => {
        this.surgeLeft = ms
        this.surgeMs = ms
    }

    update = (dtMs: number) => {
        const dt = Math.min(dtMs, 100) / 1000
        const gust = this.surgeLeft > 0 ? this.surgeLeft / this.surgeMs : 0
        this.surgeLeft = Math.max(0, this.surgeLeft - dtMs)
        const boost = 1 + 1.6 * gust
        this.embers.forEach(ember => {
            ember.life += dt * boost
            ember.y -= ember.rise * dt * boost
            if (ember.life >= ember.maxLife || ember.y < -12) {
                this.spawn(ember)
            }
        })
        this.paint(gust)
    }

    private spawn = (ember: Ember) => {
        const { width, height, spawnBand, minSize, maxSize, minRise, maxRise, sway, minLife, maxLife, random } =
            this.opts
        ember.x = random() * width
        ember.y = height - random() * height * spawnBand
        ember.rise = minRise + random() * (maxRise - minRise)
        ember.swayAmp = sway * (0.4 + random() * 0.6)
        ember.swayHz = 0.25 + random() * 0.5
        ember.phase = random() * Math.PI * 2
        ember.life = 0
        ember.maxLife = minLife + random() * (maxLife - minLife)
        ember.size = minSize + random() * (maxSize - minSize)
    }

    private paint = (gust: number = 0) => {
        this.embers.forEach(ember => {
            const age = ember.life / ember.maxLife
            const twinkle = 0.78 + 0.22 * Math.sin(ember.life * 9 + ember.phase)
            const { sprite } = ember
            sprite.x = ember.x + ember.swayAmp * Math.sin(ember.life * ember.swayHz * Math.PI * 2 + ember.phase)
            sprite.y = ember.y
            const size = ember.size * (1 - 0.45 * age)
            sprite.width = size
            sprite.height = size
            sprite.tint = sparkColor(age)
            sprite.alpha = Math.min(1, emberEnvelope(age) * twinkle * this.opts.alpha * (1 + 0.5 * gust))
        })
    }
}

// ---- a burst of sparks --------------------------------------------------------------------------

interface Spark {
    sprite: Sprite
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
}

/**
 * A handful of sparks thrown up from a point, falling back as they cool. A reward beat -- "that
 * landed" -- and gone in well under a second. `done` once the last spark is out; the owner removes it.
 */
export class SparkBurst extends Container {
    private sparks: Spark[] = []

    constructor(
        count: number = 12,
        { spread = 70, lift = 110, random = Math.random }: { spread?: number; lift?: number; random?: () => number } = {},
    ) {
        super()
        for (let i = 0; i < count; i++) {
            const sprite = new Sprite(sparkTexture())
            sprite.anchor.set(0.5)
            sprite.blendMode = BLEND_MODES.ADD
            this.addChild(sprite)
            const angle = -Math.PI / 2 + (random() - 0.5) * Math.PI * 1.3
            const speed = 0.45 + random() * 0.55
            this.sparks.push({
                sprite,
                vx: Math.cos(angle) * spread * speed * 1.4,
                vy: Math.sin(angle) * lift * speed - 20,
                life: 0,
                maxLife: 0.45 + random() * 0.4,
                size: 6 + random() * 7,
            })
        }
        this.update(0)
    }

    get done(): boolean {
        return this.sparks.every(spark => spark.life >= spark.maxLife)
    }

    update = (dtMs: number) => {
        const dt = Math.min(dtMs, 100) / 1000
        this.sparks.forEach(spark => {
            spark.life += dt
            spark.vy += 160 * dt
            const { sprite } = spark
            sprite.x += spark.vx * dt
            sprite.y += spark.vy * dt
            const age = Math.min(1, spark.life / spark.maxLife)
            const size = spark.size * (1 - 0.5 * age)
            sprite.width = size
            sprite.height = size
            sprite.tint = sparkColor(age)
            sprite.alpha = age >= 1 ? 0 : 1 - age * age
        })
    }
}

/** Bursts of sparks a screen has thrown, advanced each frame and dropped once they are out. */
export class Sparks {
    private live: SparkBurst[] = []

    throw = (parent: Container, x: number, y: number, count: number = 12, spread?: number, lift?: number) => {
        const burst = new SparkBurst(count, { spread, lift })
        burst.position.set(x, y)
        parent.addChild(burst)
        this.live.push(burst)
    }

    update = (dtMs: number) => {
        this.live.forEach(burst => burst.update(dtMs))
        this.live = this.live.filter(burst => {
            if (burst.done) {
                burst.parent?.removeChild(burst)
                return false
            }
            return true
        })
    }

    clear = () => {
        this.live.forEach(burst => burst.parent?.removeChild(burst))
        this.live = []
    }
}

